import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

const API_URL = "https://sunrisemeditechsystems.com/api"; // Define your backend API URL

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState("buy"); // 'buy' or 'rent'
  const [quantity, setQuantity] = useState(1);
  const [rentalStartDate, setRentalStartDate] = useState(new Date());
  const [rentalEndDate, setRentalEndDate] = useState(new Date());
  const [showOptions, setShowOptions] = useState(false); // New state to control visibility
  const [currentCalculatedPrice, setCurrentCalculatedPrice] = useState(0); // New state for dynamic price
  const [selectedAction, setSelectedAction] = useState(null); // 'cart' or 'wishlist'
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isInCart, setIsInCart] = useState(false); // New state
  const [cartItemDetails, setCartItemDetails] = useState(null); // New state
  const [availableForRent, setAvailableForRent] = useState(0);
  const detail = localStorage.getItem("wishlist_detail");

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      if (selectedOption === "buy") {
        setCurrentCalculatedPrice(product.salePrice * quantity);
      } else {
        const timeDiff = rentalEndDate.getTime() - rentalStartDate.getTime();
        const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
        const days = diffDays > 0 ? diffDays : 1;
        setCurrentCalculatedPrice(product.rentalPrice * days);
      }
    }
  }, [selectedOption, quantity, rentalStartDate, rentalEndDate, product]);

  useEffect(() => {
    if (selectedOption === "rent" && rentalStartDate && rentalEndDate) {
      axios
        .get(`${API_URL}/products/${product.id}/rental-availability`, {
          params: {
            start: rentalStartDate.toISOString(),
            end: rentalEndDate.toISOString(),
          },
        })
        .then((res) => {
          setAvailableForRent(res.data.available);
        });
    }
  }, [selectedOption, rentalStartDate, rentalEndDate, product]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/${id}`);
      console.log("Fetched product:", response.data);
      setProduct(response.data);
      setLoading(false);

      // Check if product is already in wishlist
      const userId = localStorage.getItem("currentUserId");
      const wishlistKey = `wishlist_${userId}`;
      const existingWishlist = JSON.parse(
        localStorage.getItem(wishlistKey) || "[]"
      );
      setIsInWishlist(
        existingWishlist.some((item) => item.productId === response.data.id)
      );

      // Check if product is already in cart
      const cartKey = `cart_${userId}`;
      const existingCart = JSON.parse(localStorage.getItem(cartKey) || "[]");
      const foundInCart = existingCart.find(
        (item) => item.productId === response.data.id
      );
      if (foundInCart) {
        setIsInCart(true);
        setCartItemDetails(foundInCart);
        setSelectedOption(foundInCart.type); // Set initial option based on cart
        if (foundInCart.type === "buy") {
          setQuantity(foundInCart.quantity);
        } else {
          setRentalStartDate(new Date(foundInCart.rentalStartDate));
          setRentalEndDate(new Date(foundInCart.rentalEndDate));
        }
      } else {
        setIsInCart(false);
        setCartItemDetails(null);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      Swal.fire("Error", "Failed to fetch product details", "error");
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (selectedOption === "buy" && quantity > product.saleStock) {
      Swal.fire("Error", "Not enough stock available for purchase.", "error");
      return;
    }
    if (selectedOption === "rent" && product.rentalStock <= 0) {
      Swal.fire("Error", "Not enough stock available for rent.", "error");
      return;
    }

    const timeDiff = rentalEndDate.getTime() - rentalStartDate.getTime();
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const rentalDays = diffDays > 0 ? diffDays : 1;

    const cartItem = {
      productId: product.id,
      name: product.name,
      price: selectedOption === "buy" ? product.salePrice : product.rentalPrice,
      quantity: selectedOption === "buy" ? quantity : rentalDays,
      type: selectedOption,
      image: product.image,
    };

    if (selectedOption === "rent") {
      cartItem.rentalStartDate = rentalStartDate.toISOString();
      cartItem.rentalEndDate = rentalEndDate.toISOString();
    }

    // Get existing cart items from localStorage
    const userId = localStorage.getItem("currentUserId");
    const cartKey = `cart_${userId}`;
    const existingCart = JSON.parse(localStorage.getItem(cartKey) || "[]");

    // Check if item already exists in cart
    const existingItemIndex = existingCart.findIndex(
      (item) =>
        item.productId === cartItem.productId && item.type === cartItem.type
    );

    if (existingItemIndex > -1) {
      // Update quantity if item exists
      existingCart[existingItemIndex].quantity += cartItem.quantity;
    } else {
      // Add new item if it doesn't exist
      existingCart.push(cartItem);
    }

    // Save updated cart to localStorage
    const updatedCart = existingCart;
    localStorage.setItem(cartKey, JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated")); // Dispatch event

    Swal.fire({
      title: "Success!",
      text: "Product added to cart",
      icon: "success",
      showConfirmButton: false,
      timer: 1500,
    });
    setShowOptions(false); // Hide options after adding to cart
    setQuantity(1); // Reset quantity
    setRentalStartDate(new Date());
    setRentalEndDate(new Date());
    setSelectedOption("buy"); // Reset option
    setSelectedAction(null); // Reset selected action
    setIsInCart(true); // Update cart status
    setCartItemDetails(cartItem); // Update cart item details
  };

  const handleAddToWishlist = () => {
    if (!product) return;

    const wishlistItem = {
      productId: product.id,
      name: product.name,
      price: product.salePrice, // Wishlist should probably just store a base price
      image: product.image,
      quantity: 1, // Always add quantity
      type: selectedOption || "buy", // Always add type
    };

    const userId = localStorage.getItem("currentUserId");
    const wishlistKey = `wishlist_${userId}`;
    const existingWishlist = JSON.parse(
      localStorage.getItem(wishlistKey) || "[]"
    );
    const existingItemIndex = existingWishlist.findIndex(
      (item) => item.productId === wishlistItem.productId
    );

    if (existingItemIndex > -1) {
      Swal.fire("Info", "Product is already in your wishlist", "info");
    } else {
      existingWishlist.push(wishlistItem);
      localStorage.setItem(wishlistKey, JSON.stringify(existingWishlist));
      setIsInWishlist(true);
      Swal.fire({
        title: "Success!",
        text: "Product added to wishlist",
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
      });
    }
     navigate("/wishlist")
    // Reset state after adding
    setShowOptions(false);
    setSelectedAction(null);
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container text-center py-5">
        <h2>Product not found</h2>
        <button
          className="btn btn-primary mt-3"
          onClick={() => navigate("/shop")}
        >
          Back to Shop
        </button>
      </div>
    );
  }

  const saleStock = product?.saleStock ?? 0;
  const rentalStock = product?.rentalStock ?? 0;
  const isBuyOutOfStock = saleStock <= 0;
  const isRentOutOfStock = rentalStock <= 0;

  return (
    <>
      {/* BREADCRUMB AREA START */}
      <div
        className="ltn__breadcrumb-area text-left bg-overlay-white-30 bg-image"
        data-bs-bg="img/bg/sunrise-medical.jpg"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12" style={{ padding: "0 6%" }}>
              <div className="ltn__breadcrumb-inner">
                <h1 className="page-title">Product Details</h1>
                <div className="ltn__breadcrumb-list">
                  <ul>
                    <li>
                      <Link to="/" className="bg-black-color">
                        <span className="ltn__secondary-color">
                          <i className="fas fa-home bg-black-color" />
                        </span>{" "}
                        Home
                      </Link>
                    </li>
                    <li>
                      <a className="bg-black-color" href="/shop">
                        Shop
                      </a>
                    </li>
                    <li>{product.name}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* BREADCRUMB AREA END */}

      {/* PRODUCT DETAILS AREA START */}
      <div className="ltn__product-details-area pb-70">
        <div className="container-fluid" style={{ padding: "0 6%" }}>
          <div className="row">
            <div className="col-lg-4 col-md-12" style={{ marginRight: "42px" }}>
              <div className="ltn__product-details-img-gallery">
                <div className="ltn__product-details-img-gallery-inner">
                  <div
                    className="ltn__product-details-img-1"
                    style={{
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={`https://sunrisemeditechsystems.com${product.image}`}
                      alt={product.name}
                      style={{
                        width: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  {/* <div className="ltn__product-details-img-2" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    {product.image.slice(1).map((image, index) => (
                      <img key={index} src={`https://sunrisemeditechsystems.com/${image}`} alt={`${product.name} ${index + 2}`} style={{ width: '80px', height: '80px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #ddd' }} />
                    ))}
                  </div> */}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-12">
              <div className="modal-product-info shop-details-info pl-0">
                <h3>{product.name}</h3>
                <div className="product-price">
                  <span>₹{currentCalculatedPrice.toFixed(2)}</span>
                </div>
                {/* Buy/Rent Options */}
                <div className="d-flex align-items-center mb-3">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`theme-btn-1 btn btn-effect-1 ${
                        selectedOption === "buy" ? "" : "btn-outline"
                      }`}
                      style={{ marginRight: "10px" }}
                      onClick={() => setSelectedOption("buy")}
                    >
                      Buy
                    </button>
                    {product.isRentalAvailable && (
                      <button
                        type="button"
                        className={`theme-btn-1 btn btn-effect-1 ${
                          selectedOption === "rent" ? "" : "btn-outline"
                        }`}
                        onClick={() => setSelectedOption("rent")}
                      >
                        Rent
                      </button>
                    )}
                  </div>
                </div>

                <div className="modal-product-meta ltn__product-details-menu-1">
                  <ul>
                    <li>
                      <strong>Categories:</strong>
                      <span>
                        <a href="#">{product.category}</a>
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Date Pickers for Rent */}
                {selectedOption === "rent" && (
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      <label className="fw-bold">From:</label>
                      <DatePicker
                        selected={rentalStartDate}
                        onChange={(date) => setRentalStartDate(date)}
                        selectsStart
                        startDate={rentalStartDate}
                        endDate={rentalEndDate}
                        className="form-control input-date"
                      />
                    </div>
                    <div>
                      <label className="fw-bold">To:</label>
                      <DatePicker
                        selected={rentalEndDate}
                        onChange={(date) => setRentalEndDate(date)}
                        selectsEnd
                        startDate={rentalStartDate}
                        endDate={rentalEndDate}
                        minDate={rentalStartDate}
                        className="form-control input-date"
                      />
                    </div>
                  </div>
                )}

                {/* Quantity for Buy */}
                {selectedOption === "buy" && (
                  <div className="ltn__product-details-menu-2">
                    <ul>
                      <li>
                        <div className="cart-plus-minus">
                          <div
                            className="dec qtybutton"
                            onClick={() =>
                              setQuantity((prev) => (prev > 1 ? prev - 1 : 1))
                            }
                          >
                            -
                          </div>
                          <input
                            type="text"
                            value={quantity}
                            readOnly
                            className="cart-plus-minus-box"
                          />
                          <div
                            className="inc qtybutton"
                            onClick={() =>
                              setQuantity((prev) =>
                                prev < saleStock ? prev + 1 : prev
                              )
                            }
                            style={{
                              pointerEvents: isBuyOutOfStock ? "none" : "auto",
                              opacity: isBuyOutOfStock ? 0.5 : 1,
                            }}
                          >
                            +
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "0.95em",
                            color: "#888",
                            marginTop: 4,
                          }}
                        >
                          {isBuyOutOfStock ? (
                            <span style={{ color: "red" }}>Out of Stock</span>
                          ) : (
                            `In Stock: ${saleStock}`
                          )}
                        </div>
                      </li>
                    </ul>
                  </div>
                )}

                {/* Rent Out of Stock Message */}
                {selectedOption === "rent" && (
                  <div
                    style={{
                      fontSize: "0.95em",
                      color: "#888",
                      marginBottom: 8,
                    }}
                  >
                    {isRentOutOfStock ? (
                      <span style={{ color: "red" }}>Out of Stock</span>
                    ) : (
                      `Available for Rent: ${rentalStock}`
                    )}
                  </div>
                )}

                <div className="ltn__product-details-menu-2">
                  <ul>
                    {detail === "wishlist" ? (
                      <li>
                        <a
                          href="#"
                          className="theme-btn-1 btn btn-effect-1"
                          title="Add to Wishlist"
                          onClick={(e) => {
                            e.preventDefault();
                            if (isBuyOutOfStock && selectedOption === "buy")
                              return;
                            if (isRentOutOfStock && selectedOption === "rent")
                              return;
                            isInWishlist ? navigate("/wishlist") : handleAddToWishlist();
                          }}
                          style={{
                            pointerEvents:
                              (selectedOption === "buy" && isBuyOutOfStock) ||
                              (selectedOption === "rent" && isRentOutOfStock)
                                ? "none"
                                : "auto",
                            opacity:
                              (selectedOption === "buy" && isBuyOutOfStock) ||
                              (selectedOption === "rent" && isRentOutOfStock)
                                ? 0.5
                                : 1,
                          }}
                        >
                          Add to Wishlist
                        </a>
                      </li>
                    ) : (
                      <>
                        <li>
                          <a
                            href="#"
                            className="theme-btn-1 btn btn-effect-1"
                            title={isInCart ? "Go to Cart" : "Add to Cart"}
                            onClick={(e) => {
                              e.preventDefault();
                              if (isBuyOutOfStock && selectedOption === "buy")
                                return;
                              if (isRentOutOfStock && selectedOption === "rent")
                                return;
                              isInCart ? navigate("/cart") : handleAddToCart();
                            }}
                            style={{
                              pointerEvents:
                                (selectedOption === "buy" && isBuyOutOfStock) ||
                                (selectedOption === "rent" && isRentOutOfStock)
                                  ? "none"
                                  : "auto",
                              opacity:
                                (selectedOption === "buy" && isBuyOutOfStock) ||
                                (selectedOption === "rent" && isRentOutOfStock)
                                  ? 0.5
                                  : 1,
                            }}
                          >
                            {isInCart ? "Go to Cart" : "Add to Cart"}
                          </a>
                        </li>
                        {/* <li>
                          <Link
                            to="#"
                            className="theme-btn-1 btn btn-effect-1"
                            title={
                              isInWishlist
                                ? "Go to Wishlist"
                                : "Add to Wishlist"
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              isInWishlist
                                ? navigate("/wishlist")
                                : handleAddToWishlist();
                            }}
                          >
                            {isInWishlist ? (
                              "Go to Wishlist"
                            ) : (
                              <i className="far fa-heart" />
                            )}
                          </Link>
                        </li> */}
                      </>
                    )}
                  </ul>
                </div>
                <hr />
                <div className="ltn__social-media">
                  <ul>
                    <li>Share:</li>
                    <li>
                      <a href="#" title="Facebook">
                        <i className="fab fa-facebook-f" />
                      </a>
                    </li>
                    <li>
                      <a href="#" title="Twitter">
                        <i className="fab fa-twitter" />
                      </a>
                    </li>
                    <li>
                      <a href="#" title="Linkedin">
                        <i className="fab fa-linkedin-in" />
                      </a>
                    </li>
                    <li>
                      <a href="#" title="Instagram">
                        <i className="fab fa-instagram" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* PRODUCT DETAILS AREA END */}
    </>
  );
}
